import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { transfersService } from '../services/transfersService';
import { Transfer, TransferStatus, UserRole } from '../types';
import { TransferDetailModal } from '../components/TransferDetailModal';
import { RootState } from '../store/store';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, addWeeks, subWeeks,
  isSameMonth, isSameDay, parseISO,
} from 'date-fns';
import { tr } from 'date-fns/locale';

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  [TransferStatus.AVAILABLE]:   { bg: 'bg-gray-100',   text: 'text-gray-700',  dot: 'bg-gray-400' },
  [TransferStatus.CLAIMED]:     { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-400' },
  [TransferStatus.CONFIRMED]:   { bg: 'bg-blue-100',   text: 'text-blue-800',  dot: 'bg-blue-500' },
  [TransferStatus.IN_PROGRESS]: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  [TransferStatus.COMPLETED]:   { bg: 'bg-green-100',  text: 'text-green-800', dot: 'bg-green-500' },
  [TransferStatus.CANCELLED]:   { bg: 'bg-red-100',    text: 'text-red-700',   dot: 'bg-red-400' },
};

const STATUS_LABELS: Record<string, string> = {
  [TransferStatus.AVAILABLE]:   'Müsait',
  [TransferStatus.CLAIMED]:     'Talep Edildi',
  [TransferStatus.CONFIRMED]:   'Onaylandı',
  [TransferStatus.IN_PROGRESS]: 'Devam Ediyor',
  [TransferStatus.COMPLETED]:   'Tamamlandı',
  [TransferStatus.CANCELLED]:   'İptal',
};

const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const DAYS_TR_FULL = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

function getStatusColors(status: string) {
  return STATUS_COLORS[status] ?? { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' };
}

function getTransfersForDay(transfers: Transfer[], day: Date): Transfer[] {
  return transfers
    .filter(t => { try { return isSameDay(parseISO(t.transferDate), day); } catch { return false; } })
    .sort((a, b) => (a.transferTime || '').localeCompare(b.transferTime || ''));
}

/* ── Transfer chip (used in desktop month & desktop week) ── */
const TransferChip: React.FC<{ transfer: Transfer; onClick: (t: Transfer) => void }> = ({ transfer, onClick }) => {
  const colors = getStatusColors(transfer.status);
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(transfer); }}
      className={`w-full text-left px-1.5 py-0.5 rounded text-xs truncate leading-5 ${colors.bg} ${colors.text} hover:brightness-95 transition-all`}
      title={`${transfer.customerName} — ${transfer.pickupLocation} → ${transfer.dropoffLocation}`}
    >
      {transfer.transferTime && (
        <span className="font-semibold mr-1">{transfer.transferTime.slice(0, 5)}</span>
      )}
      {transfer.customerName}
    </button>
  );
};

/* ── Transfer row card (used in mobile week & day detail modal) ── */
const TransferCard: React.FC<{ transfer: Transfer; onClick: (t: Transfer) => void }> = ({ transfer, onClick }) => {
  const colors = getStatusColors(transfer.status);
  return (
    <button
      onClick={() => onClick(transfer)}
      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 active:bg-gray-100 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate text-sm">{transfer.customerName}</div>
          <div className="text-xs text-gray-500 truncate mt-0.5">
            {transfer.pickupLocation} → {transfer.dropoffLocation}
          </div>
          {transfer.flightCode && (
            <div className="text-xs text-gray-400 mt-0.5">{transfer.flightCode}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {transfer.transferTime && (
            <span className="text-xs font-bold text-gray-800">{transfer.transferTime.slice(0, 5)}</span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}>
            {STATUS_LABELS[transfer.status] || transfer.status}
          </span>
        </div>
      </div>
    </button>
  );
};

/* ── Day detail modal ── */
const DayDetailModal: React.FC<{
  day: Date;
  transfers: Transfer[];
  onClose: () => void;
  onSelect: (t: Transfer) => void;
}> = ({ day, transfers, onClose, onSelect }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
    <div
      className="bg-white rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md max-h-[85vh] flex flex-col"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h3 className="font-bold text-gray-900 text-lg capitalize">
            {format(day, 'd MMMM yyyy', { locale: tr })}
          </h3>
          <p className="text-sm text-gray-500">{transfers.length} transfer</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-xl">×</button>
      </div>
      <div className="overflow-y-auto flex-1 p-4 space-y-2">
        {transfers.map(t => (
          <TransferCard key={t.id} transfer={t} onClick={tr => { onClose(); onSelect(tr); }} />
        ))}
      </div>
    </div>
  </div>
);

/* ════════════════════════════════════════ */

interface CalendarPageProps { embedded?: boolean; }

export const CalendarPage: React.FC<CalendarPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const [calendarView, setCalendarViewRaw] = useState<'month' | 'week'>(() => {
    try {
      const saved = sessionStorage.getItem('calendar_display_view');
      if (saved === 'month' || saved === 'week') return saved;
    } catch {}
    return window.innerWidth < 768 ? 'week' : 'month';
  });
  const setCalendarView = (v: 'month' | 'week') => {
    sessionStorage.setItem('calendar_display_view', v);
    setCalendarViewRaw(v);
  };

  const [currentDate, setCurrentDate] = useState(new Date());
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TransferStatus | ''>('');
  const [dayDetailDate, setDayDetailDate] = useState<Date | null>(null);

  const canEditTransfer =
    user?.role === UserRole.MANAGER || user?.role === UserRole.ORG_ADMIN ||
    user?.role === UserRole.PLATFORM_ADMIN ||
    (user?.role === UserRole.STAFF && user?.transferPermissions?.includes('edit'));

  const visibleRange = useCallback(() => {
    if (calendarView === 'month') {
      return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    }
    return {
      start: startOfWeek(currentDate, { weekStartsOn: 1 }),
      end: endOfWeek(currentDate, { weekStartsOn: 1 }),
    };
  }, [calendarView, currentDate]);

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = visibleRange();
      const result = await transfersService.getAll({
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
        ...(statusFilter ? { status: statusFilter as TransferStatus } : {}),
        limit: '200',
      });
      setTransfers(result.data);
    } catch (err) {
      console.error('Failed to load transfers:', err);
    } finally {
      setLoading(false);
    }
  }, [visibleRange, statusFilter]);

  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  const today = new Date();

  const goBack    = () => calendarView === 'month' ? setCurrentDate(d => subMonths(d, 1)) : setCurrentDate(d => subWeeks(d, 1));
  const goForward = () => calendarView === 'month' ? setCurrentDate(d => addMonths(d, 1)) : setCurrentDate(d => addWeeks(d, 1));
  const goToday   = () => setCurrentDate(new Date());

  const headerLabel = () => {
    if (calendarView === 'month') return format(currentDate, 'MMMM yyyy', { locale: tr });
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    const we = endOfWeek(currentDate, { weekStartsOn: 1 });
    return ws.getMonth() === we.getMonth()
      ? `${format(ws, 'd')} – ${format(we, 'd MMMM yyyy', { locale: tr })}`
      : `${format(ws, 'd MMM', { locale: tr })} – ${format(we, 'd MMM yyyy', { locale: tr })}`;
  };

  const buildMonthDays = () => {
    const ms = startOfMonth(currentDate), me = endOfMonth(currentDate);
    const start = startOfWeek(ms, { weekStartsOn: 1 });
    const end   = endOfWeek(me,   { weekStartsOn: 1 });
    const days: Date[] = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  };

  const buildWeekDays = () => {
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  };

  const handleTransferClick = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setIsDetailModalOpen(true);
  };

  const totalByStatus = Object.values(TransferStatus)
    .map(s => ({ status: s, count: transfers.filter(t => t.status === s).length }))
    .filter(s => s.count > 0);

  /* ── MONTH VIEW ── */
  const MonthView = () => {
    const days = buildMonthDays();
    const MAX_CHIPS = 3;
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {DAYS_TR.map(d => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 divide-x divide-gray-100">
          {days.map((day, idx) => {
            const dayTransfers = getTransfersForDay(transfers, day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, today);
            const visible = dayTransfers.slice(0, MAX_CHIPS);
            const overflow = dayTransfers.length - MAX_CHIPS;

            return (
              <div
                key={idx}
                onClick={() => dayTransfers.length > 0 && setDayDetailDate(day)}
                className={`border-b border-gray-100 flex flex-col
                  ${!isCurrentMonth ? 'bg-gray-50' : 'bg-white'}
                  ${isToday ? 'ring-inset ring-2 ring-purple-400' : ''}
                  ${dayTransfers.length > 0 ? 'cursor-pointer' : ''}`}
              >
                {/* Day number */}
                <div className="flex justify-end p-1 md:p-1.5">
                  <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full
                    ${isToday ? 'bg-purple-600 text-white' : isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}`}>
                    {format(day, 'd')}
                  </span>
                </div>

                {/* MOBILE: dot indicators */}
                <div className="flex flex-wrap gap-0.5 justify-center pb-1 px-0.5 md:hidden min-h-[18px]">
                  {dayTransfers.slice(0, 5).map((t, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full ${getStatusColors(t.status).dot}`} />
                  ))}
                  {dayTransfers.length > 5 && (
                    <span className="text-gray-400 text-xs leading-none">+</span>
                  )}
                </div>

                {/* DESKTOP: chips */}
                <div className="hidden md:flex flex-col gap-0.5 flex-1 pb-1.5 px-1.5">
                  {visible.map(transfer => (
                    <TransferChip key={transfer.id} transfer={transfer} onClick={handleTransferClick} />
                  ))}
                  {overflow > 0 && (
                    <button
                      onClick={e => { e.stopPropagation(); setDayDetailDate(day); }}
                      className="text-xs text-purple-600 font-medium pl-0.5 text-left hover:text-purple-800 hover:underline"
                    >
                      +{overflow} daha
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ── WEEK VIEW ── */
  const WeekView = () => {
    const days = buildWeekDays();
    return (
      <>
        {/* MOBILE: vertical list */}
        <div className="md:hidden bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm divide-y divide-gray-100">
          {days.map((day, idx) => {
            const dayTransfers = getTransfersForDay(transfers, day);
            const isToday = isSameDay(day, today);
            return (
              <div key={idx} className={`${isToday ? 'bg-purple-50' : ''}`}>
                {/* Day header row */}
                <div className={`flex items-center gap-3 px-4 py-3 ${isToday ? 'bg-purple-100' : 'bg-gray-50'}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0
                    ${isToday ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                    {format(day, 'd')}
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${isToday ? 'text-purple-800' : 'text-gray-800'}`}>
                      {DAYS_TR_FULL[idx]}
                    </div>
                    <div className={`text-xs ${isToday ? 'text-purple-600' : 'text-gray-500'}`}>
                      {format(day, 'd MMMM', { locale: tr })}
                    </div>
                  </div>
                  {dayTransfers.length > 0 && (
                    <span className="ml-auto text-xs text-gray-500 font-medium">{dayTransfers.length} transfer</span>
                  )}
                </div>
                {/* Transfers */}
                {dayTransfers.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Transfer yok</div>
                ) : (
                  <div className="px-3 py-2 space-y-2">
                    {dayTransfers.map(t => (
                      <TransferCard key={t.id} transfer={t} onClick={handleTransferClick} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* DESKTOP: 7 columns */}
        <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="grid grid-cols-7 border-b border-gray-200">
            {days.map((day, idx) => {
              const isToday = isSameDay(day, today);
              return (
                <div key={idx} className={`py-3 text-center border-r last:border-r-0 border-gray-100 ${isToday ? 'bg-purple-50' : 'bg-gray-50'}`}>
                  <div className="text-xs font-semibold text-gray-500 uppercase">{DAYS_TR[idx]}</div>
                  <div className={`text-sm font-bold mt-0.5 w-7 h-7 mx-auto flex items-center justify-center rounded-full
                    ${isToday ? 'bg-purple-600 text-white' : 'text-gray-800'}`}>
                    {format(day, 'd')}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 divide-x divide-gray-100 min-h-[200px]">
            {days.map((day, idx) => {
              const dayTransfers = getTransfersForDay(transfers, day);
              const isToday = isSameDay(day, today);
              return (
                <div key={idx} className={`p-1.5 flex flex-col gap-0.5 ${isToday ? 'bg-purple-50/40' : ''}`}>
                  {dayTransfers.length === 0
                    ? <span className="text-xs text-gray-300 text-center mt-2">—</span>
                    : dayTransfers.map(t => (
                        <TransferChip key={t.id} transfer={t} onClick={handleTransferClick} />
                      ))
                  }
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  /* ── RENDER ── */
  return (
    <div className="space-y-3">
      {/* Top bar: title/toggle + navigation */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {!embedded && <h1 className="text-2xl font-bold text-gray-900">Takvim</h1>}
          {/* Aylık / Haftalık toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setCalendarView('month')}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors
                ${calendarView === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}
            >
              Aylık
            </button>
            <button
              onClick={() => setCalendarView('week')}
              className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors
                ${calendarView === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}
            >
              Haftalık
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as TransferStatus | '')}
            className="text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 max-w-[140px]"
          >
            <option value="">Tüm Durumlar</option>
            {Object.values(TransferStatus).map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button onClick={goBack}    className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 text-lg">‹</button>
            <button onClick={goToday}   className="px-3 h-9 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Bugün</button>
            <button onClick={goForward} className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 text-lg">›</button>
          </div>
        </div>
      </div>

      {/* Period title + count */}
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-gray-800 capitalize">{headerLabel()}</h2>
        <span className="text-sm text-gray-400">{transfers.length} transfer</span>
      </div>

      {/* Status pills */}
      {totalByStatus.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {totalByStatus.map(({ status, count }) => {
            const colors = getStatusColors(status);
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(prev => prev === status ? '' : status as TransferStatus)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all
                  ${colors.bg} ${colors.text}
                  ${statusFilter === status ? 'ring-2 ring-offset-1 ring-purple-500' : 'opacity-80 hover:opacity-100'}`}
              >
                <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                {STATUS_LABELS[status] || status} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Calendar */}
      {loading ? (
        <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      ) : (
        calendarView === 'month' ? <MonthView /> : <WeekView />
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        {Object.values(TransferStatus).map(status => {
          const colors = getStatusColors(status);
          return (
            <span key={status} className="flex items-center gap-1">
              <span className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
              {STATUS_LABELS[status]}
            </span>
          );
        })}
      </div>

      {/* Day detail modal */}
      {dayDetailDate && (
        <DayDetailModal
          day={dayDetailDate}
          transfers={getTransfersForDay(transfers, dayDetailDate)}
          onClose={() => setDayDetailDate(null)}
          onSelect={handleTransferClick}
        />
      )}

      {/* Transfer detail modal */}
      {selectedTransfer && isDetailModalOpen && (
        <TransferDetailModal
          transfer={selectedTransfer}
          onClose={() => { setIsDetailModalOpen(false); setSelectedTransfer(null); }}
          onEdit={canEditTransfer ? () => navigate(`/app/transfers/${selectedTransfer.id}`) : undefined}
        />
      )}
    </div>
  );
};
